import { Portal as ArkPortal } from '@ark-ui/react/portal'
import { Select as ArkSelect, createListCollection } from '@ark-ui/react/select'
import clsx from 'clsx'
import type { ComponentProps, PropsWithChildren } from 'react'
import { IconButton } from '#lib/ui/button/icon-button'
import type { UiIconName } from '#lib/ui/icon/icon'
import { Icon } from '#lib/ui/icon/icon'
import { inputRootStyles } from './input-root.css'
import { type SelectVariants, selectStyles as styles } from './select.css'

type CollectionItem = string | object

interface Props<T extends CollectionItem>
  extends Omit<ComponentProps<typeof ArkSelect.Root<T>>, 'collection'>,
    SelectVariants {
  required?: boolean
  placeholder?: string
  icon?: UiIconName
  fullWidth?: boolean
  error?: boolean | string
  isInsideModal?: boolean // Bug workaround https://github.com/chakra-ui/ark/issues/1782
  items: T[]
  itemToElement?: (item: T) => React.ReactNode
  itemToString?: (item: T) => string
  itemToValue?: (item: T) => string
}

const defaultGetItemLabel = (item: string | object) => {
  if (typeof item === 'string') {
    return item
  }

  if ('label' in item && typeof item.label === 'string') {
    return item.label as string
  }

  throw new Error(
    'items in <Select> component must be strings or objects with a "label" property',
  )
}

const defaultGetItemValue = (item: string | object) => {
  if (typeof item === 'string') {
    return item
  }

  if (
    'value' in item &&
    (typeof item.value === 'string' || typeof item.value === 'number')
  ) {
    return item.value.toString()
  }

  throw new Error(
    'items in <Select> component must be strings or objects with a "value" property',
  )
}

export function Select<T extends CollectionItem>({
  className,
  variant,
  required,
  placeholder,
  icon,
  fullWidth,
  error,
  isInsideModal,
  itemToElement,
  items,
  itemToString = defaultGetItemLabel,
  itemToValue = defaultGetItemValue,
  ...props
}: Props<T>) {
  const hasErrorMessage =
    typeof error !== 'boolean' && typeof error !== 'undefined'

  return (
    <>
      <ArkSelect.Root
        collection={createListCollection({
          items,
          itemToString,
          itemToValue,
        })}
        {...props}
        className={clsx(styles.root, className)}
        data-full-width={Boolean(fullWidth)}
        data-valid={!error}
      >
        <ArkSelect.Control className={styles.control}>
          <ArkSelect.Trigger className={styles.trigger({ variant })}>
            {itemToElement ? (
              <ArkSelect.Context<T>>
                {select => {
                  const selectedItem = select.collection.find(select.value[0])

                  if (!selectedItem)
                    return (
                      <>
                        {icon && <Icon icon={icon} size="sm" />}
                        <ArkSelect.ValueText
                          className={styles.valueText}
                          placeholder={placeholder}
                        />
                      </>
                    )

                  return itemToElement(selectedItem)
                }}
              </ArkSelect.Context>
            ) : (
              <>
                {icon && <Icon icon={icon} size="sm" />}
                <ArkSelect.ValueText
                  className={styles.valueText}
                  placeholder={placeholder}
                />
              </>
            )}

            <ArkSelect.Indicator asChild className={styles.indicator}>
              <Icon icon="chevron-down" size="md" />
            </ArkSelect.Indicator>
          </ArkSelect.Trigger>

          {!required && (
            <ArkSelect.ClearTrigger asChild>
              <IconButton icon="close" className={styles.clearTrigger} />
            </ArkSelect.ClearTrigger>
          )}
        </ArkSelect.Control>

        <PortalWrapper isInsideModal={isInsideModal}>
          <ArkSelect.Positioner
            style={{
              zIndex: 20,
            }}
          >
            <ArkSelect.Context<T>>
              {select => (
                <ArkSelect.Content className={styles.content}>
                  {select.collection.items.map(item => (
                    <ArkSelect.Item
                      key={itemToValue(item)}
                      item={item}
                      className={styles.item}
                    >
                      {itemToElement ? (
                        itemToElement(item)
                      ) : (
                        <ArkSelect.ItemText className={styles.itemText}>
                          {itemToString(item)}
                        </ArkSelect.ItemText>
                      )}
                      <ArkSelect.ItemIndicator
                        className={styles.itemIndicator}
                        asChild
                      >
                        <Icon icon="check" size="xs" />
                      </ArkSelect.ItemIndicator>
                    </ArkSelect.Item>
                  ))}
                </ArkSelect.Content>
              )}
            </ArkSelect.Context>
          </ArkSelect.Positioner>
        </PortalWrapper>
      </ArkSelect.Root>

      {hasErrorMessage && (
        <span className={inputRootStyles.errorMessage}>{error}</span>
      )}
    </>
  )
}

export function PortalWrapper(
  props: PropsWithChildren<{ isInsideModal?: boolean }>,
) {
  if (!props.isInsideModal) {
    return <ArkPortal>{props.children}</ArkPortal>
  }
  return <div className={styles.portalMock}>{props.children}</div>
}
